import { Loader } from '@mantine/core';
import { Check, CreditCard, TriangleAlert, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { FC, ReactElement, useState } from 'react';
import useSWRImmutable from 'swr/immutable';

import CircularLoader from '@/components/CircularLoader.tsx';
import PatientRow from '@/components/searchPatient/PatientRow.tsx';

import useConfig from '@/hooks/useConfig.ts';

import { CardReaderStep } from '@/models/CardReader.ts';
import { NormalizedPatient, PatientCardType } from '@/models/Patient.ts';
import { CardReadActionType, GetCardReadersResponse } from '@/models/doc-cirrus/DCCardReader.ts';

import { getCardreadersForInstances } from '@/services/cardReadService.ts';

import { useWalkInStore } from '@/stores/walkInStore.ts';

import { applyCardReadAction, triggerCardRead } from '@/utils/cardReadUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

import { SelectableList } from './SelectableList.tsx';

const CardReader: FC = () => {
    const [selected_item, setSelectedItem] = useState<string>('');
    const { instance_ids, customer_id } = useConfig();
    const card_read_state = useWalkInStore((state) => state.card_read_state);
    const is_modal_loading = useWalkInStore((state) => state.is_loading);
    const selected_instance_id = useWalkInStore((state) => state.selected_instance_id);

    const instance_ids_to_use = selected_instance_id ? [selected_instance_id] : instance_ids;

    // "adjust state during rendering" pattern to prevent unnecessary re-renders
    const [prevStep, setPrevStep] = useState(card_read_state?.step);
    if (card_read_state?.step !== prevStep) {
        setPrevStep(card_read_state?.step);
        if (!card_read_state || card_read_state.step === CardReaderStep.SELECT_READER) {
            setSelectedItem('');
        }
    }

    const {
        data,
        isLoading: is_loading,
        error,
    } = useSWRImmutable<GetCardReadersResponse, Error>(
        {
            key: 'card-readers',
            instance_ids: instance_ids_to_use,
            customer_id: customer_id,
        },
        () => getCardreadersForInstances(instance_ids_to_use, customer_id)
    );

    const card_readers = data?.card_readers ?? [];

    const handleSelectMatchingPatient = async (key: string): Promise<void> => {
        const pvs_user_id: string = key.split('::')[1];

        const patient = card_read_state?.patients.find((p) => p.user_id === pvs_user_id);
        if (!card_read_state || !patient) return;
        await applyCardReadAction(card_read_state, patient, CardReadActionType.MATCHING_SELECT, customer_id);
    };

    const handleSelect = async (value: string): Promise<void> => {
        const new_value: string = selected_item === value ? '' : value;
        setSelectedItem(new_value);

        await triggerCardRead(selected_item, card_readers, customer_id, new_value);
    };

    const getCurrentStep = (): ReactElement => {
        if (is_modal_loading) {
            return (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex h-[20rem] flex-col items-center justify-center gap-4"
                >
                    <CircularLoader size="lg" />
                    <p className="text-slate-500">Bitte warten...</p>
                </motion.div>
            );
        }

        switch (card_read_state?.step) {
            case CardReaderStep.RETRY: {
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex h-full flex-col items-center justify-center gap-4"
                    >
                        <div className="flex size-14 items-center justify-center rounded-full bg-red-100">
                            <X className="size-7 text-red-600" strokeWidth={2.5} />
                        </div>
                        <div className="text-center">
                            <p className="text-base font-medium text-slate-700">
                                Es gab ein Problem beim Lesen der Karte
                            </p>
                            <p className="mt-1 text-sm text-slate-500">Bitte versuchen Sie es erneut</p>
                        </div>
                    </motion.div>
                );
            }
            case CardReaderStep.SUCCESS: {
                const patient = card_read_state?.patients[0];
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col items-center gap-3 pt-16"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.1 }}
                            className="flex size-14 items-center justify-center rounded-full bg-emerald-500 shadow-md shadow-emerald-200"
                        >
                            <Check className="size-7 text-white" strokeWidth={3} />
                        </motion.div>
                        <div className="text-center">
                            <p className="text-sm text-slate-500">Karte erfolgreich gelesen</p>
                            {patient && (
                                <p className="mt-1 text-lg font-semibold text-slate-800">
                                    {patient.name.last_name} {patient.name.first_name}
                                </p>
                            )}
                        </div>
                    </motion.div>
                );
            }
            case CardReaderStep.MERGED: {
                const patient = card_read_state?.patients[0];
                const diff_fields = card_read_state?.diff_fields;
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex h-full flex-col items-center justify-center gap-4"
                    >
                        <div className="flex size-14 items-center justify-center rounded-full bg-amber-100">
                            <TriangleAlert className="size-7 text-amber-600" strokeWidth={2} />
                        </div>
                        <div className="text-center">
                            <p className="text-sm text-slate-500">Die Karte enthält aktualisierte Daten</p>
                            {patient && (
                                <p className="mt-1 text-base font-semibold text-slate-800">
                                    {patient.name.last_name} {patient.name.first_name}
                                </p>
                            )}
                        </div>
                        {diff_fields && diff_fields.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.15 }}
                                className="w-full max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                            >
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-slate-200 bg-slate-50">
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">
                                                Feld
                                            </th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">
                                                Alte Daten
                                            </th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">
                                                Neue Daten
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {diff_fields.map((diff_field, index) => (
                                            <tr
                                                key={diff_field.label}
                                                className={cn(
                                                    'border-b border-slate-100 last:border-b-0',
                                                    index % 2 === 1 && 'bg-slate-50/50'
                                                )}
                                            >
                                                <td className="px-4 py-3 text-sm font-medium text-slate-700">
                                                    {diff_field.label}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="rounded bg-red-50 px-2 py-1 text-sm text-red-600 line-through">
                                                        {diff_field.old_value}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="rounded bg-emerald-50 px-2 py-1 text-sm font-medium text-emerald-700">
                                                        {diff_field.new_value}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </motion.div>
                        )}
                    </motion.div>
                );
            }
            case CardReaderStep.MATCHING: {
                const patient_count = card_read_state?.patients.length ?? 0;
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex h-full w-full flex-col items-center gap-4"
                    >
                        <div className="flex size-14 items-center justify-center rounded-full bg-amber-100">
                            <TriangleAlert className="size-7 text-amber-600" strokeWidth={2} />
                        </div>
                        <div className="text-center">
                            <p className="text-base font-medium text-slate-700">
                                {patient_count} {patient_count > 1 ? 'Patienten' : 'Patient'} gefunden
                            </p>
                            <p className="mt-1 text-sm text-slate-500">Bitte wählen Sie einen Patienten aus</p>
                        </div>
                        {card_read_state?.patients[0] &&
                            card_read_state?.possible_actions.includes(CardReadActionType.MATCHING_SELECT) && (
                                <div className="w-full">
                                    <SelectableList
                                        handleSelect={handleSelectMatchingPatient}
                                        selectedItem={selected_item}
                                        classNames={{
                                            wrapper: 'w-full',
                                        }}
                                    >
                                        {card_read_state.patients.map((patient) => (
                                            <PatientRow
                                                key={`card-read-patient::${patient.user_id}`}
                                                patient={patient as NormalizedPatient}
                                                matches={[]}
                                                patientCardType={PatientCardType.CARD_READ}
                                            />
                                        ))}
                                    </SelectableList>
                                </div>
                            )}
                    </motion.div>
                );
            }
            case CardReaderStep.CANCELLED: {
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex h-full flex-col items-center justify-center gap-4"
                    >
                        <div className="flex size-14 items-center justify-center rounded-full bg-slate-100">
                            <X className="size-7 text-slate-500" strokeWidth={2.5} />
                        </div>
                        <div className="text-center">
                            <p className="text-base font-medium text-slate-700">Vorgang abgebrochen</p>
                            <p className="mt-1 text-sm text-slate-500">Der Kartenlesevorgang wurde abgebrochen</p>
                        </div>
                    </motion.div>
                );
            }
            default: {
                if (is_loading && !error)
                    return (
                        <div className="flex h-[20rem] items-center justify-center">
                            <Loader />
                        </div>
                    );

                if (!is_loading && card_readers.length === 0)
                    return (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex h-full flex-col items-center justify-center gap-3"
                        >
                            <div className="flex size-14 items-center justify-center rounded-full bg-slate-100">
                                <CreditCard className="size-7 text-slate-400" />
                            </div>
                            <p className="text-sm text-slate-500">Keine Kartenlesegeräte gefunden</p>
                        </motion.div>
                    );

                return (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col gap-4">
                        <div className="mb-2">
                            <p className="text-sm font-medium text-slate-500">Wählen Sie ein Kartenlesegerät aus</p>
                        </div>
                        <AnimatePresence>
                            <SelectableList
                                title="Kartenlesegeräte"
                                handleSelect={handleSelect}
                                selectedItem={selected_item}
                            >
                                {card_readers.map((reader) => (
                                    <div
                                        key={`${reader.tenant.id}::${reader.workplace.id}`}
                                        className="flex items-center gap-3"
                                    >
                                        <div className="flex size-10 items-center justify-center rounded-lg bg-slate-100">
                                            <CreditCard className="size-5 text-slate-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-slate-700">{reader.terminals[0].name}</p>
                                            <p className="text-sm text-slate-500">{reader.tenant.name}</p>
                                        </div>
                                    </div>
                                ))}
                            </SelectableList>
                        </AnimatePresence>
                    </motion.div>
                );
            }
        }
    };

    const render_step = getCurrentStep();

    return <div className="h-full">{render_step}</div>;
};

export default CardReader;
